import { useState } from "react";
import {
  listMyRecurringPasses,
  requestRecurringPass,
  type RequestPassPayload,
} from "./api";
import { useAdminResource } from "../admin/use-admin-resource";
import { useToast } from "../../components/useToast";

type PassType = RequestPassPayload["pass_type"];

const INITIAL = {
  fullName: "",
  purpose: "",
  passType: "monthly" as PassType,
  validFrom: "",
  validUntil: "",
  phone: "",
  email: "",
};

export function HostPassRequestForm() {
  const { showSuccess, showError } = useToast();
  const { data: passes, refresh } = useAdminResource(() => listMyRecurringPasses(), []);
  const [form, setForm] = useState(INITIAL);
  const [pending, setPending] = useState(false);

  async function submit() {
    if (!form.fullName.trim()) return showError("Visitor name is required");
    if (!form.purpose.trim()) return showError("Purpose is required");
    if (form.passType === "custom" && (!form.validFrom || !form.validUntil)) {
      return showError("A custom pass needs a start and end date");
    }
    setPending(true);
    try {
      await requestRecurringPass({
        full_name: form.fullName.trim(),
        purpose: form.purpose.trim(),
        pass_type: form.passType,
        valid_from: form.validFrom || undefined,
        valid_until: form.passType === "custom" ? form.validUntil || undefined : undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
      });
      showSuccess("Pass request submitted — HR/Admin will review it");
      setForm(INITIAL);
      await refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to submit request");
    } finally {
      setPending(false);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Request a Long-term Pass</h2>
        <span>For frequent visitors</span>
      </header>
      <p className="management-note">
        Request a reusable badge for a visitor who comes in regularly (a contractor, a vendor). HR or
        Admin approve it, and it becomes one badge they can scan to enter any day within the period.
      </p>
      <form
        className="action-form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="checkin-fields">
          <input
            value={form.fullName}
            onChange={(event) => setForm((p) => ({ ...p, fullName: event.target.value }))}
            placeholder="Visitor name *"
          />
          <input
            value={form.purpose}
            onChange={(event) => setForm((p) => ({ ...p, purpose: event.target.value }))}
            placeholder="Purpose *"
          />
          <select
            className="search-input"
            value={form.passType}
            onChange={(event) => setForm((p) => ({ ...p, passType: event.target.value as PassType }))}
            aria-label="Pass duration"
          >
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="permanent">Permanent</option>
            <option value="custom">Custom date range</option>
          </select>
          <input
            type="date"
            value={form.validFrom}
            onChange={(event) => setForm((p) => ({ ...p, validFrom: event.target.value }))}
            aria-label="Valid from (defaults to today)"
            title="Valid from (defaults to today)"
          />
          {form.passType === "custom" ? (
            <input
              type="date"
              value={form.validUntil}
              onChange={(event) => setForm((p) => ({ ...p, validUntil: event.target.value }))}
              aria-label="Valid until"
              title="Valid until"
            />
          ) : null}
        </div>
        <button className="refresh-btn action-submit" type="submit" disabled={pending}>
          {pending ? "Requesting…" : "Request Pass"}
        </button>
      </form>

      {(passes ?? []).length > 0 ? (
        <div className="table-wrap" style={{ marginTop: "1rem" }}>
          <table>
            <thead>
              <tr>
                <th>Holder</th>
                <th>Duration</th>
                <th>Validity</th>
                <th>Status</th>
                <th>Pass link</th>
              </tr>
            </thead>
            <tbody>
              {(passes ?? []).map((p) => (
                <tr key={p.id}>
                  <td>{p.visitor_name}</td>
                  <td>{p.pass_type}</td>
                  <td>
                    {p.pass_type === "permanent"
                      ? `${p.valid_from} · no expiry`
                      : `${p.valid_from} → ${p.valid_until ?? "—"}`}
                  </td>
                  <td>{p.status}</td>
                  <td>
                    {p.pass_url ? (
                      <a href={p.pass_url} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </article>
  );
}
